import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

// Types
interface WishlistItem {
  _id: string;
  instructorId: string;
  userId: string;
  createdAt: string;
}

interface WishlistResponse {
  success: boolean;
  instructorWishlist: WishlistItem[];
}

// API functions
async function fetchInstructorWishlist(): Promise<string[]> {
  const response = await fetch("/api/wishlist/instructors", {
    credentials: "include",
  });

  if (response.status === 401) {
    return [];
  }

  if (!response.ok) {
    throw new Error("Failed to fetch instructor wishlist");
  }

  const data: WishlistResponse = await response.json();
  // Map instructorId field from API response
  return data.instructorWishlist.map(
    (item: WishlistItem) =>
      item.instructorId || (item as { itemId?: string }).itemId || ""
  );
}

// Custom hooks

/**
 * Hook to fetch and manage instructor wishlist
 */
export function useInstructorWishlist() {
  return useQuery<string[], Error>({
    queryKey: ["wishlist", "instructors"],
    queryFn: fetchInstructorWishlist,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      if (error.message.includes("401")) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to check if a specific instructor is in wishlist
 */
export function useIsInstructorInWishlist(instructorId: string) {
  const { data: wishlist = [] } = useInstructorWishlist();
  return wishlist.includes(instructorId);
}

/**
 * Hook to toggle instructor wishlist status (add/remove)
 */
export function useToggleInstructorWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instructorId, action }: { instructorId: string; action: "add" | "remove" }) => {
      const response = await fetch("/api/wishlist/instructors", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ instructorId, action }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update wishlist");
      }

      await response.json();
      return {
        action: action === "add" ? "added" : ("removed" as "added" | "removed"),
        instructorId,
      };
    },
    onMutate: async ({ instructorId, action }) => {
      // Cancel outgoing refetches
      const queryKey = ["wishlist", "instructors"];
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousWishlist = queryClient.getQueryData<string[]>(queryKey);

      // Optimistically update
      queryClient.setQueryData<string[]>(queryKey, (old = []) => {
        if (action === "remove") {
          return old.filter((id) => id !== instructorId);
        } else {
          // Avoid duplicates just in case
          return old.includes(instructorId) ? old : [...old, instructorId];
        }
      });

      return { previousWishlist, queryKey };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousWishlist);
      }
      toast.error(error.message || "Failed to update wishlist");
    },
    onSuccess: (data) => {
      if (data.action === "added") {
        toast.success("Instructor added to wishlist");
      } else {
        toast.success("Instructor removed from wishlist");
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["wishlist", "instructors"] });
    },
  });
}
